import { FlowType, type FlowTypeValueType } from '../../Avo/Avo'
import { resolveLanguage, resolveLocale } from '../lib/locale'
import type { BookingState, ScreenType } from '../store'

export interface BookingToolRootProps {
  env: 'tst' | 'acc' | 'prd'
  language?: string
  /**
   * Two-letter country code, lowercase (e.g. 'nl'). Pre-selects the store
   * locator's country dropdown and drives the analytics locale.
   *
   * Lowest-priority country source — `?countryCode=`, the host page's locale
   * path (`/en-NL/`) and a full `language` locale ("en-NL") all outrank it.
   * See `resolveCountryCode`.
   */
  location?: string
  /** Predict Spring store id — analytics only, does not pre-select a store. */
  storeId?: string
  /** Pre-select a store — skips StoreLocator and lands on EmployeesList */
  locationId?: string
  locationCode?: string
  /** Pre-select an employee — lands on Timeslots */
  employeeId?: string
  /** Used for reschedule/cancel flows — fetches appointment data by booking ID */
  bookingId?: string
  /**
   * Overrides the initial screen when combined with bookingId.
   * - 'timeslots' (default when bookingId present) → reschedule flow
   * - 'cancel' → cancel flow: auto-cancels and shows confirmation
   */
  initialScreen?: 'timeslots' | 'cancel'
  /** When set, skips the pre-appointment purpose screen */
  serviceId?: string
  /** CRM customer id. With `signature`, prefills the user-data form. */
  userId?: string
  /** Auth token for the customer lookup. Useless without `userId`. */
  signature?: string
  /**
   * Host's resolved analytics page type (Avo's `pageType`), e.g. the
   * storefront's own route-derived value. `syncSystemProperties()` sends `booking_tool`
   * when absent — the standalone app has no host page.
   */
  pageType?: string
  onClose?: () => void
}

/** The screen a set of host/URL props lands on — always explicit. */
export function propsToInitialScreen({
  locationId,
  locationCode,
  employeeId,
  bookingId,
  initialScreen,
}: BookingToolRootProps): ScreenType {
  if (bookingId && initialScreen === 'cancel') return 'cancel'
  // booking-id / screen=timeslot → reschedule; employee-id → timeslots
  if (bookingId || initialScreen === 'timeslots' || employeeId) {
    return 'timeslots'
  }
  // locationId/locationCode → skip store locator, land on employees list
  if (locationId || locationCode) return 'employees-list'

  return 'store-locator'
}

/**
 * `booking_tool_opened`'s `flowType`: `cancel` when the cancel deep-link
 * resolves, `reschedule` whenever a `bookingId` is present (the only way this
 * flow reaches the timeslots pre-selected), otherwise `new`.
 *
 * Deliberately keyed off `bookingId` rather than `propsToInitialScreen`'s
 * `'timeslots'` result — an `employeeId`-only deep-link also lands on
 * `timeslots` and is a new booking, not a reschedule.
 */
export function propsToFlowType(
  props: BookingToolRootProps,
): FlowTypeValueType {
  if (props.bookingId && props.initialScreen === 'cancel')
    return FlowType.CANCEL
  if (props.bookingId) return FlowType.RESCHEDULE
  return FlowType.NEW
}

/**
 * Host-owned fields, re-applied whenever props change.
 *
 * Only written when the host actually passes a value: after mount these same
 * fields hold what the customer picked inside the tool (StoreLocator writes
 * `locationCode`, EmployeesList writes `employeeId`), so blanking them from
 * absent props would drop the selection on any unrelated prop change.
 */
export function propsToHostFields({
  env,
  language,
  location,
  storeId,
  locationId,
  locationCode,
  employeeId,
  bookingId,
  serviceId,
  userId,
  signature,
  pageType,
}: BookingToolRootProps): Partial<BookingState> {
  // Always resolved, never conditional on the prop: the page's locale path and
  // `?language=` both outrank it, so there is a language to apply even when the
  // host passes none. See resolveLanguage.
  //
  // `locale` is resolved here rather than at the point of use so it records
  // where the customer entered from. Both resolvers are pure functions of these
  // props plus the URL, so re-applying them on a prop change cannot blank a
  // value the flow has since moved on from. See resolveLocale.
  const updates: Partial<BookingState> = {
    env,
    language: resolveLanguage(language),
    locale: resolveLocale(language, location),
  }

  if (location) updates.location = location
  if (storeId) updates.storeId = storeId
  if (locationId) updates.wwLocationId = locationId
  if (locationCode) updates.locationCode = locationCode
  if (employeeId) updates.employeeId = employeeId
  if (bookingId) updates.bookingId = bookingId
  if (serviceId) updates.serviceId = serviceId
  if (userId) updates.userId = userId
  if (signature) updates.signature = signature
  if (pageType) updates.pageType = pageType

  return updates
}

/**
 * Full seed for a fresh open: every deep-link field written — `undefined`
 * included — and an explicit screen.
 *
 * The store is a module-level singleton shared by every entry point on the
 * page, so a field left out here keeps the previous open's value. That is what
 * put the storefront's footer entry on the employees list: the store page's
 * entry had seeded a `locationCode` and the `employees-list` screen, and props
 * that pre-select nothing had nothing to say about either.
 */
export function propsToStoreSeed(
  props: BookingToolRootProps,
): Partial<BookingState> {
  return {
    ...propsToHostFields(props),
    storeId: props.storeId,
    wwLocationId: props.locationId,
    locationCode: props.locationCode,
    employeeId: props.employeeId,
    bookingId: props.bookingId,
    serviceId: props.serviceId,
    userId: props.userId,
    signature: props.signature,
    screen: propsToInitialScreen(props),
    screenHistory: [],
  }
}
